import React, { useEffect, useState } from "react";
import { Button, Icon } from "@blueprintjs/core";
import norwAir from "./assets/norwAir.png";
import { ScreenProps } from "../../SequenceController";
import { ScreenConfig } from "../Sequences";
import { CrossoverScreenResult, CrossoverScreenInitialResult } from "./CrossoverResultTypes";
import { useLanguage } from "../../../../context/LanguageContext";
import { DefaultOption } from "../../../../translations/experiments/Default";
import { colors } from "../../../../components/colors";

function CookieBanner({ selected, setSelected, isDefault, t }: {
    selected: number,
    setSelected: (idx: number) => void,
    isDefault: (idx: number) => boolean,
    t: any
}) {
    const options = t.cookieBanner.options;
    return (
        <div style={{
            maxWidth: 700,
            minWidth: 340,
            margin: "34px auto 0 auto",
            background: colors.white,
            borderRadius: 16,
            boxShadow: "0 4px 32px 0 rgba(0,0,0,0.10)",
            padding: "24px 32px 36px 32px",
            position: "relative",
            minHeight: 250,
            display: "flex",
            flexDirection: "column",
            justifyContent: "flex-start"
        }}>
            <div style={{ position: "absolute", left: 24, top: 24 }}>
                <Icon icon={t.cookieBanner.icon} color={colors.brillantBlue} style={{ fontSize: 30 }} />
            </div>
            <div style={{ textAlign: "center", marginBottom: 14 }}>
                <span style={{
                    fontWeight: 700,
                    fontSize: 18,
                    letterSpacing: "0.01em",
                    display: "inline-block",
                    marginBottom: 7,
                    color: colors.brillantBlue
                }}>
                    {t.cookieBanner.title}
                </span>
                <div style={{
                    fontSize: 15.5,
                    color: colors.textMedium,
                    margin: "0 auto",
                    maxWidth: 650,
                    textAlign: "justify",
                    lineHeight: 1.5,
                }}>
                    {t.cookieBanner.description}
                </div>
            </div>
            <div style={{
                display: 'flex',
                gap: '22px',
                justifyContent: 'center',
                marginTop: 16,
                marginBottom: 3
            }}>
                {options.map((opt: any, idx: number) => (
                    <Button
                        key={idx}
                        large
                        fill
                        outlined={selected !== idx}
                        intent={selected === idx ? "primary" : "none"}
                        style={{
                            minWidth: 175,
                            border: selected === idx ? `2.5px solid ${colors.brillantBlue}` : "1px solid #bbb",
                            background: selected === idx ? colors.brillantBlue : colors.white,
                            fontWeight: 600,
                            fontSize: 16,
                            boxShadow: selected === idx ? "0 0 10px #137cbd44" : undefined,
                            transition: "border 0.2s, box-shadow 0.2s",
                            color: selected === idx ? colors.white : colors.textDark,
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            justifyContent: "center",
                            position: "relative"
                        }}
                        onClick={() => setSelected(idx)}
                        tabIndex={idx + 1}
                    >
                        {selected === idx && (
                            <Icon
                                icon="tick"
                                color={colors.white}
                                style={{
                                    position: "absolute",
                                    top: 10,
                                    right: 10,
                                    fontSize: 20,
                                    background: colors.brillantBlue,
                                    borderRadius: "50%",
                                    padding: 3,
                                    boxShadow: "0 0 4px #137cbd66"
                                }}
                            />
                        )}
                        <span>
                            {opt.label}
                            {opt.hint && (
                                <span style={{
                                    display: "block",
                                    fontSize: 13,
                                    color: selected === idx ? colors.white : colors.lichtBlau,
                                    fontWeight: 400,
                                    marginTop: 2
                                }}>
                                    {opt.hint}
                                </span>
                            )}
                        </span>
                    </Button>
                ))}
            </div>
            <div style={{ textAlign: "center", marginTop: 14 }}>
                <span style={{ fontSize: 13, color: colors.lichtBlau }}>
                    <Icon icon="info-sign" style={{ fontSize: 14, marginRight: 4 }} />
                    {t.cookieBanner.info}
                </span>
            </div>
        </div>
    );
}

export function CookieScreenRejectDefault(props: ScreenProps) {
    const defaultOption = 1;
    const [firstRenderTimestamp, setFirstRenderTimestamp] = useState(0);
    const [selected, setSelected] = useState<number>(defaultOption);
    const { setCanContinue, setResult } = props;
    const { language } = useLanguage();
    const t = DefaultOption[language].screens.screen2;

    useEffect(() => {
        setFirstRenderTimestamp(Date.now());
        setCanContinue(true);
        setResult(() => ({ ...CrossoverScreenInitialResult }));
    }, [setCanContinue, setResult]);

    useEffect(() => {
        setCanContinue(true);
        setResult((result: CrossoverScreenResult) => {
            result.selections = [];
            result.selections.push({
                choice: selected,
                millisecondsSinceFirstRender: Date.now() - firstRenderTimestamp,
                defaultOption
            });
            return result;
        });
    }, [selected, setCanContinue, setResult, firstRenderTimestamp, defaultOption]);

    const isDefault = (idx: number) => idx === defaultOption;

    return (
        <div className="experiment-container" style={{
            maxWidth: 1250,
            margin: "0 auto",
            padding: "24px 20px 44px",
            backgroundColor: colors.backgroundLight
        }}>
            {/* Content card - contains header and instructions */}
            <div style={{
                background: colors.white,
                borderRadius: 14,
                boxShadow: "0 2px 12px rgba(0,0,0,0.09)",
                padding: "24px 40px 28px",
                marginBottom: 28
            }}>
                <h2 style={{
                    color: colors.brillantBlue,
                    margin: "0 0 22px 0",
                    fontSize: 27,
                    fontWeight: 700,
                    textAlign: "center"
                }}>
                    {t.introTitle || ""}
                </h2>
                <div style={{
                    fontSize: 17,
                    lineHeight: 1.7,
                    color: colors.textMedium,
                    textAlign: "justify",
                    textJustify: "inter-word"
                }}>
                    {t.intro.map((p: string, i: number) => (
                        <p key={i} style={{ margin: i === 0 ? "0 0 10px 0" : "10px 0 0 0" }}>{p}</p>
                    ))}
                </div>
            </div>
            {/* Airline Logo below the intro text, above the Hinweis */}
            <div style={{ textAlign: "center", marginBottom: 18 }}>
                <img
                    src={norwAir}
                    alt={t.airlineLogoAlt}
                    style={{
                        maxWidth: 420,
                        width: "90%",
                        height: "auto",
                        marginBottom: 18,
                        borderRadius: 12,
                        boxShadow: "0 2px 18px #137cbd33"
                    }}
                />
            </div>
            <div style={{
                marginBottom: 18,
                fontSize: 17,
                lineHeight: 1.7,
                color: colors.textMedium
            }}>
                <p>
                    <b>{t.beforeBookingNotice}</b>
                </p>
            </div>
            <CookieBanner
                selected={selected}
                setSelected={setSelected}
                isDefault={isDefault}
                t={t}
            />
            {/* Selection confirmation */}
            <div style={{
                textAlign: "center",
                marginTop: 32,
                padding: "12px 20px",
                fontSize: 18,
                fontWeight: 500,
                color: colors.brillantBlue,
                backgroundColor: colors.lichtBlauLighter,
                borderRadius: 8,
                display: "inline-block",
                marginLeft: "auto",
                marginRight: "auto"
            }}>
                <Icon icon="tick-circle" style={{ marginRight: 8 }} />
                {selected === 0
                    ? t.cookieBanner.options[0].label
                    : t.cookieBanner.options[1].label}
            </div>
        </div>
    );
}

export const CrossoverScreen2Config: ScreenConfig = {
    component: CookieScreenRejectDefault,
};